
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Filter, ChevronLeft, ChevronRight, Search, Download, Briefcase, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import RichTextDisplay from '@/components/RichTextDisplay';

const ACTION_COLORS = {
  create: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  approve: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  reject: 'bg-red-500/10 text-red-400 border-red-500/20',
  send: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  payment: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
  advance: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  close: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  delete: 'bg-red-500/10 text-red-400 border-red-500/20',
  cancel: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
  status_change: 'bg-teal-500/10 text-teal-400 border-teal-500/20',
  status_update: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  convert_to_project: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
  upload: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  download: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
  update: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  edit: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
};

const ENTITY_LABELS = {
  lead: 'Lead',
  proposal: 'Proposal',
  contract: 'Contract',
  project: 'Project',
  invoice: 'Invoice',
  timesheet: 'Timesheet',
  reimbursement: 'Reimbursement',
  payroll: 'Payroll',
  task: 'To-Do',
  client: 'Client',
  user: 'User',
  document: 'Document',
  subconsultant_agreement: 'Subconsultant',
  bill: 'Bill',
  accounts_payable: 'AP',
  recurring_invoice: 'Recurring Inv.',
  journal_entry: 'Journal Entry',
};

function formatTimestamp(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// Strips any embedded HTML markup from rich-text status updates so they
// render as plain readable text in the activity log. Also collapses
// whitespace, decodes a few common HTML entities, and clamps very long
// strings so the row height stays stable.
function stripHtml(str) {
  if (!str) return '';
  const s = String(str);
  // If no angle brackets, fast-path
  if (!s.includes('<') && !s.includes('&')) return s;
  let out = s
    .replace(/<\/(p|div|li|h[1-6])>/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
  if (out.length > 240) out = out.slice(0, 240) + '…';
  return out;
}

function buildDescription(item) {
  const action = item.action || '';
  const entity = ENTITY_LABELS[item.entity_type] || item.entity_type || '';
  const details = item.details || {};
  const parts = [];

  // Identifier
  const idLabel = details.invoice_number || details.contract_number || details.proposal_number || details.project_number || details.contact_name || '';
  if (idLabel) parts.push(idLabel);

  // ─── Timesheet approve / reject ────────────────────────────────────────
  // Surface WHO had their timesheet acted on, plus the entry count / hours
  // / week. (For mass approve we list each employee.)
  if (item.entity_type === 'timesheet' && (action === 'approve' || action === 'reject')) {
    // Single-employee week or single entry
    if (details.employee_name) {
      parts.push(`Employee: ${details.employee_name}`);
    }
    if (details.entries) {
      parts.push(`${details.entries} ${details.entries === 1 ? 'entry' : 'entries'}`);
    }
    if (details.hours != null) {
      parts.push(`${Number(details.hours).toFixed(2)} hrs`);
    } else if (details.hours_total != null) {
      parts.push(`${Number(details.hours_total).toFixed(2)} hrs`);
    }
    if (details.date) {
      parts.push(`Date: ${details.date}`);
    } else if (details.week_start) {
      parts.push(`Week of ${details.week_start}`);
    } else if (details.date_range_start && details.date_range_end) {
      parts.push(`${details.date_range_start} → ${details.date_range_end}`);
    }
    if (details.project_number || details.project_name) {
      parts.push(`Project: ${details.project_number || ''} ${details.project_name || ''}`.trim());
    }
    // Mass-approve: list each employee + entry/hour breakdown
    if (Array.isArray(details.employees_summary) && details.employees_summary.length > 0) {
      const summary = details.employees_summary
        .map(e => `${e.name} (${e.entries}/${Number(e.hours).toFixed(2)}h)`)
        .join(', ');
      parts.push(`Employees: ${summary}`);
    }
    if (action === 'reject' && details.reason) {
      parts.push(`Reason: "${stripHtml(details.reason)}"`);
    }
    const actionLabel = action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    return { actionLabel, entity, description: parts.join(' · ') };
  }

  // Action-specific detail
  if (action === 'advance' && details.from_stage && details.to_stage) {
    parts.push(`${details.from_stage} → ${details.to_stage}`);
  }
  if (action === 'close' && details.outcome) {
    parts.push(`Outcome: ${details.outcome}`);
  }
  if (action === 'status_change' && details.new_status) {
    parts.push(`New status: ${details.new_status}`);
  }
  if (action === 'status_update') {
    if (details.project_number) parts.push(details.project_number);
    if (details.project_name) parts.push(details.project_name);
    if (details.status_text) parts.push(`"${stripHtml(details.status_text)}"`);
  }
  if (action === 'payment' && details.amount) {
    parts.push(`$${Number(details.amount).toFixed(2)}`);
  }
  if (details.count) {
    parts.push(`${details.count} entries`);
  }
  if (details.employee) {
    parts.push(details.employee);
  }

  const actionLabel = action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return { actionLabel, entity, description: parts.join(' · ') };
}

const PAGE_SIZE = 50;

// A row in the activity log is "drillable" (clickable into the modal) when it
// represents a timesheet approve/reject and has the rich entries_snapshot
// payload. Older log entries pre-snapshot rollout cannot be drilled into.
function isDrillable(item) {
  if (!item || item.entity_type !== 'timesheet') return false;
  if (item.action !== 'approve' && item.action !== 'reject') return false;
  const snapshot = item.details?.entries_snapshot;
  return Array.isArray(snapshot) && snapshot.length > 0;
}

function formatModalTimestamp(ts) {
  if (!ts) return '-';
  try {
    const d = new Date(ts);
    return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return ts; }
}

function entryLinkage(entry) {
  if (entry.project_name) {
    return { kind: 'project', label: entry.project_name, section: entry.section_name };
  }
  if (entry.proposal_number) {
    return { kind: 'proposal', label: `Proposal ${entry.proposal_number}` };
  }
  if (entry.lead_label) {
    return { kind: 'lead', label: `Lead: ${entry.lead_label}` };
  }
  if (entry.rfp_label) {
    return { kind: 'rfp', label: `RFP: ${entry.rfp_label}` };
  }
  return { kind: 'none', label: 'No linkage' };
}

function TimesheetAuditModal({ item, onClose, canDownload }) {
  if (!item) return null;
  const d = item.details || {};
  const entries = d.entries_snapshot || [];
  const isApprove = item.action === 'approve';
  const action_label = isApprove ? 'Approved' : 'Rejected';

  const handleDownload = async () => {
    try {
      const r = await api.get(`/timesheets/approval-audit/${item.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `timesheet_${item.action}_audit_${item.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to download PDF');
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="timesheet-audit-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Timesheet {action_label} - Audit</DialogTitle>
          <DialogDescription>
            <span className="block text-xs"><span className="text-muted-foreground">{action_label} by:</span> <span className="text-foreground font-medium">{d.approver_name || item.user_name || 'Unknown'}</span></span>
            <span className="block text-xs"><span className="text-muted-foreground">Processed at:</span> <span className="text-foreground">{formatModalTimestamp(d.approved_at || item.created_at)}</span></span>
            <span className="block text-xs"><span className="text-muted-foreground">Entries:</span> <span className="text-foreground tabular-nums">{entries.length}</span> &nbsp;|&nbsp; <span className="text-muted-foreground">Total Hours:</span> <span className="text-foreground tabular-nums">{Number(d.hours_total || entries.reduce((s, e) => s + (Number(e.hours) || 0), 0)).toFixed(2)}h</span></span>
          </DialogDescription>
        </DialogHeader>

        {!isApprove && d.reason && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2.5">
            <p className="text-xs uppercase tracking-wider text-destructive font-semibold mb-0.5">Rejection reason</p>
            <p className="text-xs text-foreground/80 whitespace-pre-wrap">{d.reason}</p>
          </div>
        )}

        <div className="border border-border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs px-3 w-[100px]">Date</TableHead>
                <TableHead className="text-xs px-3">Employee</TableHead>
                <TableHead className="text-xs px-3 w-[70px] text-right">Hours</TableHead>
                <TableHead className="text-xs px-3 w-[70px] text-center">Billable</TableHead>
                <TableHead className="text-xs px-3">Linkage</TableHead>
                <TableHead className="text-xs px-3">Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((e, idx) => {
                const link = entryLinkage(e);
                return (
                  <TableRow key={e.id || idx} data-testid={`audit-entry-${idx}`}>
                    <TableCell className="px-3 text-xs font-mono tabular-nums">{e.date || '-'}</TableCell>
                    <TableCell className="px-3 text-xs">{e.employee_name || '-'}</TableCell>
                    <TableCell className="px-3 text-xs text-right tabular-nums">{Number(e.hours || 0).toFixed(2)}</TableCell>
                    <TableCell className="px-3 text-center">
                      {e.is_billable ? <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs">Y</Badge> : <Badge variant="outline" className="text-xs">N</Badge>}
                    </TableCell>
                    <TableCell className="px-3">
                      {link.kind === 'project' && (
                        <span className="text-xs">
                          <span className="inline-flex items-center gap-1 text-foreground"><Briefcase className="h-3 w-3 text-primary" />{link.label}</span>
                          {link.section && <span className="block text-muted-foreground/80 ml-4">{link.section}</span>}
                        </span>
                      )}
                      {link.kind === 'proposal' && (
                        <span className="text-xs inline-flex items-center gap-1 text-amber-400"><FileText className="h-3 w-3" />{link.label}</span>
                      )}
                      {link.kind === 'lead' && (
                        <span className="text-xs inline-flex items-center gap-1 text-sky-400"><FileText className="h-3 w-3" />{link.label}</span>
                      )}
                      {link.kind === 'rfp' && (
                        <span className="text-xs inline-flex items-center gap-1 text-purple-400"><FileText className="h-3 w-3" />{link.label}</span>
                      )}
                      {link.kind === 'none' && (
                        <span className="text-xs text-muted-foreground italic">{link.label}</span>
                      )}
                    </TableCell>
                    <TableCell className="px-3 max-w-[260px]">
                      {e.description ? <RichTextDisplay value={e.description} collapsedHeight={20} /> : <span className="text-xs text-muted-foreground/60 italic">-</span>}
                    </TableCell>
                  </TableRow>
                );
              })}
              {entries.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-xs text-muted-foreground py-6">No entry details captured.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="audit-modal-close">Close</Button>
          {canDownload && (
            <Button onClick={handleDownload} className="bg-primary text-primary-foreground" data-testid="audit-modal-download-pdf">
              <Download className="h-4 w-4 mr-1.5" /> Download PDF
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ActivityLogPage() {
  const { hasPerm } = useAuth();
  const canApproveTimesheets = !!hasPerm('approve_timesheets');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [auditModal, setAuditModal] = useState(null);

  // Filters
  const [entityType, setEntityType] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchText, setSearchText] = useState('');

  // Filter options
  const [filterOpts, setFilterOpts] = useState({ entity_types: [], actions: [], users: [] });

  useEffect(() => {
    api.get('/activity-log/filters').then(r => setFilterOpts(r.data)).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
    if (entityType) params.entity_type = entityType;
    if (actionFilter) params.action = actionFilter;
    if (userFilter) params.user_id = userFilter;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    try {
      const r = await api.get('/activity-log', { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch { setItems([]); setTotal(0); }
    setLoading(false);
  }, [page, entityType, actionFilter, userFilter, dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const clearFilters = () => {
    setEntityType(''); setActionFilter(''); setUserFilter('');
    setDateFrom(''); setDateTo(''); setSearchText(''); setPage(0);
  };

  const filtered = items.filter(i => {
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    const desc = JSON.stringify(i.details || {}).toLowerCase();
    return (i.user_name || '').toLowerCase().includes(q) ||
      (i.entity_type || '').toLowerCase().includes(q) ||
      (i.action || '').toLowerCase().includes(q) ||
      desc.includes(q);
  });

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const hasFilters = entityType || actionFilter || userFilter || dateFrom || dateTo;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="activity-log-page">
      <div className="flex items-center justify-between">
        <h1 className="flex items-baseline gap-3 text-[22px] font-medium tracking-tight">Activity Log<span className="text-sm text-muted-foreground/70 font-normal tabular-nums">{total} entries</span></h1>
      </div>

      {/* Filters */}
      <Card className="border-white/10 bg-card shadow-none">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="h-4 w-4 text-muted-foreground shrink-0" />
            <Select value={entityType || 'all'} onValueChange={v => { setEntityType(v === 'all' ? '' : v); setPage(0); }}>
              <SelectTrigger className="w-36 h-9 text-sm" data-testid="activity-entity-filter">
                <SelectValue placeholder="All Entities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-sm">All Entities</SelectItem>
                {filterOpts.entity_types.map(t => (
                  <SelectItem key={t} value={t} className="text-sm">{ENTITY_LABELS[t] || t}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={actionFilter || 'all'} onValueChange={v => { setActionFilter(v === 'all' ? '' : v); setPage(0); }}>
              <SelectTrigger className="w-36 h-9 text-sm" data-testid="activity-action-filter">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-sm">All Actions</SelectItem>
                {filterOpts.actions.map(a => (
                  <SelectItem key={a} value={a} className="text-sm">{a.replace(/_/g, ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={userFilter || 'all'} onValueChange={v => { setUserFilter(v === 'all' ? '' : v); setPage(0); }}>
              <SelectTrigger className="w-44 h-9 text-sm" data-testid="activity-user-filter">
                <SelectValue placeholder="All Users" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-sm">All Users</SelectItem>
                {filterOpts.users.map(u => (
                  <SelectItem key={u.id} value={u.id} className="text-sm">{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(0); }}
              className="w-36 h-9 text-sm" placeholder="From" data-testid="activity-date-from" />
            <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(0); }}
              className="w-36 h-9 text-sm" placeholder="To" data-testid="activity-date-to" />

            <div className="relative flex-1 min-w-[160px]">
              <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
              <Input value={searchText} onChange={e => setSearchText(e.target.value)}
                placeholder="Search details..." className="pl-8 h-9 text-sm" data-testid="activity-search" />
            </div>

            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 text-sm text-muted-foreground" data-testid="activity-clear-filters">
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <div className="border border-white/10 rounded-md bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[170px] px-3">Timestamp</TableHead>
              <TableHead className="w-[140px] px-3">User</TableHead>
              <TableHead className="w-[120px] px-3">Action</TableHead>
              <TableHead className="w-[100px] px-3">Entity</TableHead>
              <TableHead className="px-3">Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-sm text-muted-foreground">Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-sm text-muted-foreground">No activity entries found.</TableCell></TableRow>
            ) : (
              filtered.map(item => {
                const { actionLabel, entity, description } = buildDescription(item);
                const colorClass = ACTION_COLORS[item.action] || 'bg-muted text-muted-foreground border-border';
                const drillable = isDrillable(item);
                const canOpen = drillable && canApproveTimesheets;
                const isTimesheetReviewRow = item.entity_type === 'timesheet' && (item.action === 'approve' || item.action === 'reject');
                const onRowClick = canOpen ? () => setAuditModal(item) : undefined;

                const rowEl = (
                  <TableRow
                    key={item.id}
                    data-testid={`activity-row-${item.id}`}
                    onClick={onRowClick}
                    className={canOpen ? 'cursor-pointer hover:bg-secondary/40 transition-colors' : isTimesheetReviewRow && !canOpen ? 'cursor-not-allowed opacity-90' : ''}
                  >
                    <TableCell className="text-sm text-muted-foreground tabular-nums px-3 whitespace-nowrap">{formatTimestamp(item.created_at)}</TableCell>
                    <TableCell className="text-sm font-medium px-3 truncate max-w-[140px]">{item.user_name || 'System'}</TableCell>
                    <TableCell className="px-3">
                      <Badge className={`text-xs whitespace-nowrap ${colorClass}`}>{actionLabel}</Badge>
                    </TableCell>
                    <TableCell className="px-3">
                      <span className="text-sm text-muted-foreground">{entity}</span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground px-3 break-words">{description}</TableCell>
                  </TableRow>
                );

                // Wrap legacy timesheet rows in a tooltip explaining why they
                // can't be clicked. Only timesheet review rows get this
                // treatment - other entity types remain non-interactive.
                if (isTimesheetReviewRow && !drillable) {
                  return (
                    <TooltipProvider key={item.id}>
                      <Tooltip>
                        <TooltipTrigger asChild>{rowEl}</TooltipTrigger>
                        <TooltipContent side="left" className="bg-popover text-popover-foreground border-border max-w-xs p-2">
                          <p className="text-xs leading-relaxed">Detailed entry list isn&apos;t available for this older log row.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                }
                return rowEl;
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span className="tabular-nums">Page {page + 1} of {totalPages} ({total} entries)</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="activity-prev-page">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="activity-next-page">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <TimesheetAuditModal
        item={auditModal}
        onClose={() => setAuditModal(null)}
        canDownload={canApproveTimesheets}
      />
    </div>
  );
}
